import { ActionTypes } from "@/actions";

// Removed direct database import - using API instead
import { dataAPI } from "./api/dataApi";

import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import store from "@/store";
import { resolveCollectionIdentifier } from "./utils/collectionUtils";
import { EMPTY_ARRAY } from "@/constants/reduxStableRefs";
import { buildCollectionTableKey, normalizeOrgSegment } from "./utils/collectionPaths";

const QuickAdd = ({ onPressEnter }: { onPressEnter: any }) => {
    const dispatch = useDispatch()
    const _user = useSelector((state: any) => state.user)
    const _meta = useSelector((state: any) => state.table.meta ?? EMPTY_ARRAY)
    const [meta, setMeta] = useState((meta: any) => meta)
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState('');
    const [defaultQuickAdd, setDefaultQuickAdd] = useState<any>({})
    const { org: routeOrgParam, slug: routeSlugParam } = useParams<{ org?: string; slug?: string }>();
    const collectionSlug = (routeSlugParam || '').replace(/[!]+$/, '').trim();
    const routeOrgSeg = (routeOrgParam || '').replace(/[!]+$/, '').trim();
    const activeTableKey = buildCollectionTableKey(collectionSlug, routeOrgSeg || null);
    const allTableData = useSelector((state: any) => state.table)
    const [tableData, setTableData] = useState([])
    const [suggestions, setSuggestions] = useState([])
    const [keyToggler, setKeyToggler] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const [, setSelectedItem] = useState(null) as any
    const [justAdded, setJustAdded] = useState(false);
    const [notificationType, setNotificationType] = useState<'added' | 'updated' | null>(null);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [alwaysSet, setAlwaysSet] = useState(false);

    const getCollectionName = useCallback(() => {
        return resolveCollectionIdentifier(meta, collectionSlug) || collectionSlug || undefined;
    }, [meta, collectionSlug]);

    enum DialogResponses {
        COPY = 'COPY',
        SET = 'SET',
        ALWAYS_SET = 'ALWAYS_SET',
    }

    useEffect(() => {
        if (allTableData) {
            const data = allTableData.tables.find((t: any) => t.slug === activeTableKey)
            setTableData(data?.data)
        }
    }, [allTableData, activeTableKey])

    useEffect(() => {
        setDefaultQuickAdd({})
    }, [activeTableKey])

    useEffect(() => {
        const routeOrgNorm = normalizeOrgSegment(routeOrgSeg);
        const m = _meta.find(
            (x: any) =>
                x &&
                (x.tableKey === activeTableKey ||
                    (x.slug === collectionSlug &&
                        normalizeOrgSegment(x.organization) === routeOrgNorm))
        );

        if (typeof (m) !== 'undefined' && m) {
            setMeta(m)

            // Ensure fields is an array
            const fields = Array.isArray(m.fields) ? m.fields : []

            // Helper to check boolean values (handles true, "true", 1, etc.)
            const isTrue = (value: any) => value === true || value === 'true' || value === 1

            // Find default quick add field - first check ALL fields for quickDefault
            const defaultFieldFromAll = fields.find((f: any) => isTrue(f.quickDefault))
            if (defaultFieldFromAll) {
                console.log('QuickAdd: Found default quick add field (from all fields):', defaultFieldFromAll.name, defaultFieldFromAll)
                setDefaultQuickAdd(defaultFieldFromAll)
            } else {
                console.log('QuickAdd: No quick default field found for slug:', collectionSlug)
                console.log('QuickAdd: Available fields:', fields.map((f: any) => ({ name: f.name, quickDefault: f.quickDefault })))
                setDefaultQuickAdd({})
            }
        } else {
            console.log('QuickAdd: Meta not found for slug:', collectionSlug)
            console.log('QuickAdd: Available meta slugs:', _meta.map((m: any) => m?.slug))
            setDefaultQuickAdd({})
        }
    }, [collectionSlug, activeTableKey, routeOrgSeg, _meta])

    useEffect(() => {
        if (inputValue.length === 0) {
            setSuggestions([]);
            return;
        }

        let filteredSuggestions: any = tableData
            ?.filter((item: any) => {
                if (!defaultQuickAdd?.name) return false;
                const val = item[defaultQuickAdd.name]?.toLowerCase() || "";
                return val.includes(inputValue.toLowerCase());
            })
            .map((item: any, index: number) => ({
                ...item,
                selected: index === selectedIndex,
                isFirstMatch: index === 0 && selectedIndex === -1,
            }));

        setSuggestions(filteredSuggestions);
    }, [defaultQuickAdd, tableData, inputValue, selectedIndex])

    const addNewToState = (skipDialog?: boolean) => {
        return new Promise<void>(async (resolve) => {
            const termInSuggestions = suggestions.find((item: any) => item[defaultQuickAdd.name] === inputValue) ? true : false;
            if (!alwaysSet && !skipDialog && termInSuggestions) {
                setShowDuplicateDialog(true);
                return;
            } else {
                if (termInSuggestions && !skipDialog) {
                    quickAddUpdate();
                    return;
                }
            }

            const parseBooleanValue = (value: any) => {
                if (value === true || value === 'true' || value === 1 || value === '1') {
                    return true
                }
                if (value === false || value === 'false' || value === 0 || value === '0') {
                    return false
                }
                return undefined
            }

            const checkboxDefaults = (meta?.fields || [])
                .filter((field: any) => field?.type === 'checkbox')
                .reduce((acc: any, field: any) => {
                    const parsed = parseBooleanValue(field?.value)
                    if (parsed !== undefined && field?.name) {
                        acc[field.name] = parsed
                    }
                    return acc
                }, {})

            const initialData: any = {
                [defaultQuickAdd.name]: inputValue,
                slug: meta.slug,
                ...checkboxDefaults
            };

            await dispatch({
                type: ActionTypes.EDIT_ITEM_NEW,
                payload: {
                    initial: initialData,
                    fields: meta?.fields || [],
                    slug: meta?.slug || collectionSlug,
                    collection: getCollectionName(),
                    user: _user
                }
            });
            const state: any = store.getState();
            resolve(state.item.current)
        })
    }

    const addNewItem = (data: any) => {
        return new Promise<void>(async (resolve) => {
            try {
                // Use slug for API calls - backend resolves slug to appropriate collection
                const slugToUse = collectionSlug || getCollectionName();
                if (!slugToUse) {
                    throw new Error('Slug is required to add a new item.');
                }

                const dataToSave = { ...data };
                delete dataToSave.slug;

                const orgForApi =
                    (meta?.organization || routeOrgSeg || '').trim() ?
                        normalizeOrgSegment(meta?.organization || routeOrgSeg) :
                        undefined;

                // API uses slug - backend resolves to appropriate collection
                const response = await dataAPI.createCollectionItem(slugToUse, dataToSave, orgForApi);
                // Items use 'uid' field, not 'id'
                dataToSave.uid = response.data?.uid;

                setInputValue('');
                setJustAdded(true);

                // Set the correct notification type
                setNotificationType('added');

                // Show success feedback briefly
                setTimeout(() => {
                    setJustAdded(false);
                    setNotificationType(null);
                }, 2000);

                // After a successful create, refresh the table data for this collection
                dispatch({
                    type: ActionTypes.TABLE_REQUEST,
                    payload: {
                        slugName: slugToUse,
                        tableKey: activeTableKey,
                        organization: orgForApi,
                        user: _user,
                        component: meta,
                    }
                });

                resolve();
            } catch (error) {
                console.error('Error adding item:', error);
                resolve(); // Still resolve to prevent hanging
            }
        })
    }

    const addNew = async (skipDialog?: boolean) => {
        await addNewToState(skipDialog).then((res) => {
            addNewItem(res);
        })
    }

    const quickAddUpdate = async (always?: boolean) => {
        if (always) {
            setAlwaysSet(true);
        }
        let item;
        if (selectedIndex > -1) {
            item = suggestions[selectedIndex];
        } else {
            item = tableData.find((item: any) => item[defaultQuickAdd.name] === inputValue);
        }

        if (typeof (item) === 'undefined') return;

        console.log('Processing existing item:', item[defaultQuickAdd.name]);

        const parseBooleanValue = (value: any) => {
            if (value === true || value === 'true' || value === 1 || value === '1') {
                return true
            }
            if (value === false || value === 'false' || value === 0 || value === '0') {
                return false
            }
            return undefined
        }

        const quickSetValues = (meta?.fields || [])
            .filter((field: any) => field?.quickSetValue !== undefined && field?.quickSetValue !== null && String(field.quickSetValue).trim() !== '')
            .reduce((acc: any, field: any) => {
                const rawValue = field.quickSetValue
                if (!field?.name) {
                    return acc
                }
                if (field.type === 'checkbox') {
                    const parsed = parseBooleanValue(rawValue)
                    if (parsed !== undefined) {
                        acc[field.name] = parsed
                    }
                    return acc
                }
                if (field.type === 'number') {
                    const parsed = Number(rawValue)
                    acc[field.name] = Number.isFinite(parsed) ? parsed : rawValue
                    return acc
                }
                acc[field.name] = rawValue
                return acc
            }, {})

        await dispatch({
            type: ActionTypes.QUICK_ADD_UPDATE,
            payload: { item: item, meta: meta, quickSetValues }
        });
        onPressEnter();
        setSelectedIndex(-1)
        setSelectedItem(null)
        setInputValue('');
        setJustAdded(true);

        setNotificationType('updated');

        setSuggestions([]);
        setShowDuplicateDialog(false);
        setTimeout(() => {
            setJustAdded(false);
            setNotificationType(null);
        }, 2000);
    }

    const handleKeyDown = async (e: any) => {
        setKeyToggler(!keyToggler)

        if (e.key === "Enter" && defaultQuickAdd?.name) {
            e.preventDefault(); // Prevent default form submission on mobile
            await handleAddItem();
        }
        else if (e.key === "ArrowDown") {
            setSelectedItem(null)
            if (selectedIndex < suggestions.length - 1) {
                setSelectedIndex(selectedIndex + 1)
            } else if (selectedIndex === -1 && suggestions.length > 0) {
                // If no selection and suggestions exist, select the first one
                setSelectedIndex(0)
            }
        }
        else if (e.key === "ArrowUp") {
            setSelectedItem(null)
            if (selectedIndex > 0) {
                setSelectedIndex(selectedIndex - 1)
            } else if (selectedIndex === 0) {
                // If at first item, deselect
                setSelectedIndex(-1)
            }
        }
        else {
            setSelectedIndex(-1)
            setSelectedItem(null)
        }
    }

    const handleAddItem = async () => {
        if (!defaultQuickAdd?.name || !inputValue.trim()) return;

        // If a specific item is selected with arrow keys, use it
        if (selectedIndex > -1) {
            setSelectedItem(suggestions[selectedIndex])
            await quickAddUpdate();
            setShowDuplicateDialog(false);
            return;
        }

        // If no specific selection but suggestions exist, use the first match
        if (suggestions.length > 0) {
            const firstMatch = suggestions[0];
            setSelectedIndex(0);
            setSelectedItem(firstMatch);
            await quickAddUpdate();
            setShowDuplicateDialog(false);
            return;
        }

        // No suggestions available, create new item
        addNew();
        onPressEnter();
    }

    const clearSelection = () => {
        setSelectedItem(null)
        setSelectedIndex(-1)
        setInputValue('');
    }

    const handleDialogResponse = async (response: DialogResponses) => {
        switch (response) {
            case DialogResponses.COPY:
                await addNew(true)
                break;
            case DialogResponses.SET:
                quickAddUpdate();
                break;
            case DialogResponses.ALWAYS_SET:
                setAlwaysSet(true);
                quickAddUpdate(true);
                break;
        }
        setShowDuplicateDialog(false);
    }

    const handleClickSuggestion = (clickedItem: any, clickedIndex: number) => {
        setSelectedIndex(clickedIndex);
        setSelectedItem(clickedItem);
        quickAddUpdate();
        setShowDuplicateDialog(false);
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    }

    return <>
        {defaultQuickAdd?.name ? (
            <div className="relative">
                <div className="relative">
                    <Input
                        type="text"
                        placeholder={`Start typing to add ${defaultQuickAdd.label || defaultQuickAdd.name}...`}
                        value={inputValue}
                        onChange={handleInputChange}
                        onClick={() => clearSelection()}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        className={`
                            w-full px-4 py-3 pr-12 text-sm border border-gray-300 rounded-lg
                            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                            transition-all duration-200 bg-white text-gray-900
                            md:text-sm text-base mobile-quickadd-input
                            relative z-0
                            ${justAdded ?
                                notificationType === 'added' ? 'ring-2 ring-green-500 border-green-500 bg-green-50' :
                                    notificationType === 'updated' ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50' :
                                        'ring-2 ring-gray-500 border-gray-500 bg-gray-50'
                                : 'bg-white'
                            }
                        `}
                        style={{
                            backgroundColor: justAdded ? undefined : 'white',
                            color: '#111827',
                            zIndex: 1
                        }}
                    />

                    {/* Plus icon button for mobile */}
                    <button
                        type="button"
                        onClick={handleAddItem}
                        disabled={!inputValue.trim()}
                        className={`
                            ${inputValue.trim()
                                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }
                            ${justAdded ? 'hidden' : ''}
                            transition-all duration-200 border-none outline-none
                        `}
                        style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10
                        }}
                    >
                        <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ pointerEvents: 'none' }}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </button>

                    {justAdded && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <Badge variant="secondary" className={`text-xs ${notificationType === 'added' ? 'bg-green-100 text-green-800' :
                                notificationType === 'updated' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {notificationType === 'updated' ? 'Updated!' : 'Added!'}
                            </Badge>
                        </div>
                    )}
                </div>

                {suggestions.length > 0 && (
                    <div className={`
                        absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg 
                        max-h-60 overflow-y-auto
                        md:relative md:mt-2 md:max-h-60
                        mobile-suggestions mobile-slide-up
                    `}>
                        <div className="p-2">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2 flex items-center justify-between">
                                <span>Suggestions ({suggestions.length})</span>
                                {selectedIndex === -1 && suggestions.length > 0 && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500 normal-case">Press Enter for first match</span>
                                )}
                            </div>
                            {suggestions?.map((item: any, index: number) => (
                                <div
                                    key={item.uid}
                                    className={`
                                        px-3 py-2 rounded-md cursor-pointer transition-colors text-sm
                                        mobile-touch-target flex items-center
                                        ${item['selected']
                                            ? 'bg-blue-100 text-blue-900 border-l-2 border-blue-500 dark:bg-blue-950/55 dark:text-blue-100 dark:border-blue-400'
                                            : item['isFirstMatch']
                                                ? 'bg-gray-50 text-gray-800 border-l-2 border-gray-300 dark:bg-gray-700/50 dark:text-gray-100 dark:border-gray-500'
                                                : 'hover:bg-gray-50 text-gray-700 dark:hover:bg-gray-700/50 dark:text-gray-200'
                                        }
                                    `}
                                    onClick={() => { handleClickSuggestion(item, index) }}
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <span className="font-medium">{item[defaultQuickAdd.name]}</span>
                                        {item['selected'] && (
                                            <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 text-xs">
                                                Selected
                                            </Badge>
                                        )}
                                        {item['isFirstMatch'] && !item['selected'] && (
                                            <Badge variant="secondary" className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-xs">
                                                Press Enter
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        ) : (
            <div className="text-center py-4">
                <div className="text-sm text-gray-500 mb-2">
                    Quick Add not configured
                </div>
                <div className="text-xs text-gray-400">
                    Configure a quick field in the component editor to enable quick adding
                </div>
            </div>
        )}

        <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
            <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto mx-4 my-8">
                <DialogHeader>
                    <DialogTitle className="flex items-center space-x-2 text-lg">
                        <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <span>Duplicate Item Found</span>
                    </DialogTitle>
                    <DialogDescription className="text-gray-600 text-sm">
                        An item with the {defaultQuickAdd.label || defaultQuickAdd.name} "{inputValue}" already exists.
                        What would you like to do?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-4">
                    <Button
                        variant="outline"
                        className="w-full justify-start h-auto p-3 text-left"
                        onClick={() => handleDialogResponse(DialogResponses.COPY)}
                    >
                        <div className="flex items-start space-x-3">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 text-sm">
                                    <span className="hidden sm:inline">Create Duplicate</span>
                                    <span className="sm:hidden">+</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    <span className="hidden sm:inline">Add another item with the same {defaultQuickAdd.label || defaultQuickAdd.name}</span>
                                    <span className="sm:hidden">Create duplicate</span>
                                </div>
                            </div>
                        </div>
                    </Button>

                    <Button
                        variant="outline"
                        className="w-full justify-start h-auto p-3 text-left"
                        onClick={() => handleDialogResponse(DialogResponses.SET)}
                    >
                        <div className="flex items-start space-x-3">
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 text-sm">
                                    <span className="hidden sm:inline">Update Existing</span>
                                    <span className="sm:hidden">✓</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Update the existing item with the latest changes
                                </div>
                            </div>
                        </div>
                    </Button>

                    <Button
                        variant="outline"
                        className="w-full justify-start h-auto p-3 text-left"
                        onClick={() => handleDialogResponse(DialogResponses.ALWAYS_SET)}
                    >
                        <div className="flex items-start space-x-3">
                            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 text-sm">
                                    <span className="hidden sm:inline">Always Update (Session)</span>
                                    <span className="sm:hidden">⚡</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Always update existing items during this session</div>
                            </div>
                        </div>
                    </Button>
                </div>

                <DialogFooter className="pt-4">
                    <Button variant="ghost" onClick={() => setShowDuplicateDialog(false)} className="text-sm">
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </>
}
export default QuickAdd;